import React from "react";
import Moment from "react-moment";
import {
  Phone,
  Mail,
  MapPin,
  Briefcase,
  Building2,
  Tag,
  Wrench,
  Hash,
  User,
  Clock,
} from "lucide-react";
import "./ContactView.css";

const sectionTitleStyle = {
  fontSize: "12px",
  fontWeight: 700,
  color: "#94A3B8",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  borderBottom: "1px solid #EEF2F6",
  paddingBottom: "8px",
  marginBottom: "14px",
};

const labelStyle = {
  display: "block",
  fontSize: "12px",
  fontWeight: 700,
  color: "#2E6DA4",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  marginBottom: "4px",
};

const valueStyle = {
  fontSize: "12px",
  fontWeight: 600,
  color: "#0F2D42",
  lineHeight: 1.5,
};

const fieldGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: "18px",
};

const iconStyle = { marginRight: "4px", verticalAlign: "-1px" };

const orNA = (value) => {
  if (value === null || value === undefined) return "NA";
  if (typeof value === "string" && value.trim() === "") return "NA";
  return value;
};

const ContactInfo = ({ contactData }) => {
  if (!contactData) {
    return (
      <div className="ctv-page">
        <div className="ctv-card ctv-empty">No contact data available.</div>
      </div>
    );
  }

  const regionNames =
    contactData.regionNames || contactData.region_name || "";

  return (
    <div className="ctv-page">
      <div
        className="ctv-card ctv-card-body"
        style={{ display: "flex", flexDirection: "column", gap: "20px" }}
      >
        {/* ── Basic Information ── */}
        <div>
          <div style={sectionTitleStyle}>Basic Information</div>
          <div style={fieldGridStyle}>
            <div>
              <label style={labelStyle}>
                <User size={11} style={iconStyle} />
                Name
              </label>
              <div style={valueStyle}>{orNA(contactData.name)}</div>
            </div>
            <div>
              <label style={labelStyle}>
                <Briefcase size={11} style={iconStyle} />
                Job Title
              </label>
              <div style={valueStyle}>{orNA(contactData.job_title)}</div>
            </div>
            <div>
              <label style={labelStyle}>
                <Mail size={11} style={iconStyle} />
                Email
              </label>
              <div style={valueStyle}>{orNA(contactData.email)}</div>
            </div>
            <div>
              <label style={labelStyle}>
                <Tag size={11} style={iconStyle} />
                Contact Type
              </label>
              <div style={valueStyle}>{orNA(contactData.type)}</div>
            </div>
          </div>
        </div>

        {/* ── Contact Information ── */}
        <div>
          <div style={sectionTitleStyle}>Contact Information</div>
          <div style={fieldGridStyle}>
            <div>
              <label style={labelStyle}>
                <Phone size={11} style={iconStyle} />
                Primary Phone No.
              </label>
              <div style={valueStyle}>{orNA(contactData.phone1)}</div>
            </div>
            <div>
              <label style={labelStyle}>
                <Phone size={11} style={iconStyle} />
                Secondary Phone No.
              </label>
              <div style={valueStyle}>{orNA(contactData.phone2)}</div>
            </div>
          </div>
        </div>

        {/* ── Organisation ── */}
        <div>
          <div style={sectionTitleStyle}>Organisation</div>
          <div style={fieldGridStyle}>
            <div>
              <label style={labelStyle}>
                <Building2 size={11} style={iconStyle} />
                Agency Name
              </label>
              <div style={valueStyle}>{orNA(contactData.agency_name)}</div>
            </div>
            <div>
              <label style={labelStyle}>
                <MapPin size={11} style={iconStyle} />
                Region
              </label>
              <div style={valueStyle}>{orNA(regionNames)}</div>
            </div>
            <div>
              <label style={labelStyle}>
                <Wrench size={11} style={iconStyle} />
                Skill
              </label>
              <div style={valueStyle}>{orNA(contactData.skills)}</div>
            </div>
            <div>
              <label style={labelStyle}>
                <Hash size={11} style={iconStyle} />
                Quantity
              </label>
              <div style={valueStyle}>
                {contactData.quantity === null ||
                contactData.quantity === undefined
                  ? "NA"
                  : contactData.quantity}
              </div>
            </div>
          </div>
        </div>

        {/* ── Audit ── */}
        <div>
          <div style={sectionTitleStyle}>Audit</div>
          <div style={fieldGridStyle}>
            <div>
              <label style={labelStyle}>
                <User size={11} style={iconStyle} />
                Created By
              </label>
              <div style={valueStyle}>{orNA(contactData.created_by)}</div>
            </div>
            <div>
              <label style={labelStyle}>
                <Clock size={11} style={iconStyle} />
                Created On
              </label>
              <div style={valueStyle}>
                {contactData.created_on ? (
                  <Moment format="DD-MM-YYYY, hh:mm A">
                    {contactData.created_on}
                  </Moment>
                ) : (
                  "NA"
                )}
              </div>
            </div>
            <div>
              <label style={labelStyle}>
                <User size={11} style={iconStyle} />
                Updated By
              </label>
              <div style={valueStyle}>{orNA(contactData.updated_by)}</div>
            </div>
            <div>
              <label style={labelStyle}>
                <Clock size={11} style={iconStyle} />
                Updated On
              </label>
              <div style={valueStyle}>
                {contactData.updated_on ? (
                  <Moment format="DD-MM-YYYY, hh:mm A">
                    {contactData.updated_on}
                  </Moment>
                ) : (
                  "NA"
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Address ── */}
        <div>
          <div style={sectionTitleStyle}>Address</div>
          <div
            style={{
              ...valueStyle,
              fontWeight: 500,
              background: "#EBF4FA",
              border: "1px solid #E2E8F0",
              borderRadius: "10px",
              padding: "12px 14px",
            }}
          >
            {orNA(contactData.address)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactInfo;
